const getEntryCollection = require('./entry-collection-get');
const createEntry = require('./entry-create');
const publishEntry = require('./entry-publish');
const updateEntry = require('./entry-update');

const syncEntryBatch = (space, batch) => {
  return getEntryCollection(space, batch.map((entry) => entry.sys.id)).then((response) => {
    // Create an array of IDs of entries we got back from Contentful:
    const exisingIds = response.items.map((entry) => {
      return entry.sys.id;
    });

    // Create an array of tuples storing the original index in the batch
    // along with the entry data. This is so we can return entries in the
    // original order at the end of the function:
    const indexedBatch = batch.map((data, index) => {
      return {
        index: index,
        data: data
      };
    });

    // Create an array of entries that already exist in Contentful. These
    // should be updated with new data so we store the exising entry on the
    // tuple for use later:
    const entriesToUpdate = indexedBatch.filter(({data}) => {
      if (exisingIds.indexOf(data.sys.id) !== -1) {
        return true;
      }

      return false;
    }).map((tuple) => {
      const entry = response.items.find((entry) => entry.sys.id === tuple.data.sys.id);

      if (!entry) {
        throw new Error(`Entry with ID ${tuple.data.sys.id} not found in Contentful.`);
      }

      tuple.entry = entry;
      return tuple;
    });

    // Create an array of entries we need to create from scratch:
    const entriesToCreate = indexedBatch.filter(({data}) => {
      if (exisingIds.indexOf(data.sys.id) === -1) {
        return true;
      }

      return false;
    });

    return Promise.all([
      // Create all entries scheduled for creation and return a tuple of the
      // entry and the index its data had in the original batch:
      Promise.all(entriesToCreate.map(({index, data}) => {
        if (!data.sys.contentType.sys.id) {
          throw new Error('No type set on entry.');
        }
        const type = data.sys.contentType.sys.id;

        return createEntry(space, type, data).then((entry) => {
          return {
            entry: entry,
            index: index
          };
        });
      })),
      // Update all entries scheduled for update and return a tuple of the
      // entry and the index its data had in the original batch:
      Promise.all(entriesToUpdate.map(({index, entry, data}) => {
        return updateEntry(entry, data).then((entry) => {
          return {
            entry: entry,
            index: index
          };
        });
      }))
    ]).then(([createdEntries, updatedEntries]) => {
      const entries = createdEntries.concat(updatedEntries);

      // Reorder the tuples into the order they had in the original batch
      // and map the tuple array to an array of entries:
      return entries.sort((tupleLeft, tupleRight) => {
        return tupleLeft.index - tupleRight.index;
      }).map((tuple) => {
        return tuple.entry;
      });
    });
  });
};

const batches = (original, batchSize) => {
  const data = Array.from(original);
  const batches = [];

  while (data.length) {
    batches.push(data.slice(0, batchSize));
    data.splice(0, batchSize);
  }

  return batches;
};

module.exports = (space, data, forcePublish = false) => {
  if (!Array.isArray(data)) {
    throw new Error('Data parameter must be an array.');
  }

  return batches(data, 20).reduce((current, batch) => {
    return current.then((syncedEntries) => {
      return syncEntryBatch(
        space,
        batch
      ).then((entries) => {
        if (forcePublish) {
          // Publish the batch:
          return Promise.all(entries.map(publishEntry));
        }

        return entries;
      }).then((entries) => {
        // Add batch to list of synced entries:
        return syncedEntries.concat(entries);
      });
    });
  }, Promise.resolve([]));
};
